import { EditButton, Show } from '@/components/admin';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Eye, EyeOff, FileSpreadsheet, FileText, Mail, PackageCheck } from 'lucide-react';
import { useDataProvider, useGetOne, useNotify, useRecordContext, useRefresh, useUpdate } from 'ra-core';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { CrmDataProvider } from '../../providers/types';
import { useConfigurationContext } from '../../root/ConfigurationContext';
import type { Company, Contact, DeliveryChallan } from '../../types';
import { WatermarkOverlay } from '../shared/WatermarkOverlay';
import { DCEmailModal } from './DCEmailModal';
import { DCPDFButton } from './DCPDFButton';

// ─── Number to Words Indian format ───────────────────────────────────────────
const numberToWordsIndian = (num: number): string => {
    if (!num || isNaN(num)) return '';
    const a = ['', 'One ', 'Two ', 'Three ', 'Four ', 'Five ', 'Six ', 'Seven ', 'Eight ', 'Nine ', 'Ten ', 'Eleven ', 'Twelve ', 'Thirteen ', 'Fourteen ', 'Fifteen ', 'Sixteen ', 'Seventeen ', 'Eighteen ', 'Nineteen '];
    const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    const inWords = (n: number): string => {
        if (n === 0) return '';
        if (n < 20) return a[n];
        if (n < 100) return b[Math.floor(n / 10)] + ' ' + a[n % 10];
        if (n < 1000) return a[Math.floor(n / 100)] + 'Hundred ' + inWords(n % 100);
        if (n < 100000) return inWords(Math.floor(n / 1000)) + 'Thousand ' + inWords(n % 1000);
        if (n < 10000000) return inWords(Math.floor(n / 100000)) + 'Lakh ' + inWords(n % 100000);
        return inWords(Math.floor(n / 10000000)) + 'Crore ' + inWords(n % 10000000);
    };

    const whole = Math.floor(num);
    const decimal = Math.round((num - whole) * 100);
    let result = 'Rupees ' + inWords(whole).trim();
    if (decimal > 0) {
        result += ' and ' + inWords(decimal).trim() + ' Paise';
    }
    return result + ' Only';
};

// ─── Format Date DD/MM/YYYY ───────────────────────────────────────────────────
const formatDateIN = (dateStr?: string | null): string => {
    if (!dateStr) return '—';
    try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return dateStr;
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
    } catch {
        return dateStr;
    }
};

// ─── Format UOM helper ────────────────────────────────────────────────────────
const formatUom = (uom?: string | null): string => {
    if (!uom) return 'No.s';
    const lower = uom.trim().toLowerCase();
    if (lower === 'nos' || lower === 'no' || lower === 'no.s' || lower === 'numbers') return 'No.s';
    if (lower === 'set' || lower === 'sets') return 'Set';
    if (lower === 'kg' || lower === 'kgs') return 'KG';
    if (lower === 'mtr' || lower === 'meter' || lower === 'meters') return 'Mtr';
    if (lower === 'box' || lower === 'boxes') return 'Box';
    return uom;
};

// ─── Clean Address Display ───────────────────────────────────────────────────
const ADDRESS_PLACEHOLDER_RE =
    /^\s*(not\s*provided(\s*yet)?|to\s*be\s*provided|tbd|n\.?\/?a\.?|none|nil|null|-+|\.+)\s*$/i;

const cleanAddressText = (value?: string | null): string => {
    const v = (value ?? '').trim();
    return v && !ADDRESS_PLACEHOLDER_RE.test(v) ? v : '';
};

// ─── Export to Excel Helper ───────────────────────────────────────────────────
const exportChallanToExcel = (challan: DeliveryChallan, company?: Company | null, pi?: any, contact?: Contact | null) => {
    const ref = challan.reference || 'DC';
    const date = challan.challan_date ? new Date(challan.challan_date).toLocaleDateString('en-IN') : '';
    const items = challan.items || [];
    const totalQty = items.reduce((acc, it) => acc + (Number(it.quantity) || 0), 0);

    const escapeXml = (str?: string | null) => {
        if (!str) return '';
        return String(str)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&apos;');
    };

    const billToName = escapeXml(company?.name || '—');
    const billToAddr = escapeXml(
        cleanAddressText(challan.bill_to_address) ||
        cleanAddressText(company?.billing_address) ||
        [cleanAddressText(company?.address), [company?.city, company?.state_abbr, company?.zipcode].filter(Boolean).join(', '), company?.country].filter(Boolean).join(', ') ||
        cleanAddressText(challan.ship_to_address) ||
        '—'
    );
    const placeOfSupply = escapeXml(
        (challan as any).place_of_supply ||
        [company?.city, company?.state_abbr ? `${company.state_abbr}-${company.zipcode || ''}` : company?.zipcode].filter(Boolean).join(', ') ||
        '—'
    );
    const poRef = (challan as any).po_reference as string | undefined;
    const gstin = escapeXml(company?.tax_identifier || (company as any)?.gst_number || '—');
    const contactPerson = escapeXml(challan.contact_person || (contact ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() : '') || '—');
    const contactNo = escapeXml(challan.contact_no || contact?.phone_jsonb?.[0]?.number || (contact as any)?.phone || '—');
    const legalRef = escapeXml(
        pi?.reference ? `Reference: Material dispatched against Proforma Invoice No: ${pi.reference} – Part Supply`
        : challan.terms_conditions ? `Reference: ${challan.terms_conditions}`
        : 'Reference: Material dispatched under Delivery Challan – Part Supply'
    );

    let rowsXml = `
    <Row ss:Height="24">
        <Cell ss:MergeAcross="5" ss:StyleID="title"><Data ss:Type="String">Delivery Challan</Data></Cell>
    </Row>
    <Row>
        <Cell ss:MergeAcross="2" ss:StyleID="boldCompany"><Data ss:Type="String">Rovasys Industries</Data></Cell>
        <Cell ss:MergeAcross="2" ss:StyleID="meta"><Data ss:Type="String">Challan No - ${escapeXml(challan.reference)}</Data></Cell>
    </Row>
    <Row>
        <Cell ss:MergeAcross="2" ss:StyleID="text"><Data ss:Type="String">W 145, S BLOCK, MIDC BHOSARI, PUNE – 411026</Data></Cell>
        <Cell ss:MergeAcross="2" ss:StyleID="meta"><Data ss:Type="String">Challan Date: ${date}</Data></Cell>
    </Row>
    <Row>
        <Cell ss:MergeAcross="2" ss:StyleID="text"><Data ss:Type="String">GST NUMBER: 27ABCFR8887R1ZX</Data></Cell>
        <Cell ss:MergeAcross="2" ss:StyleID="meta"><Data ss:Type="String">Challan Type: ${escapeXml(challan.challan_type || 'Direct')}</Data></Cell>
    </Row>
    <Row>
        <Cell ss:MergeAcross="2" ss:StyleID="text"><Data ss:Type="String">Contact No : +91 8657555553</Data></Cell>
        <Cell ss:MergeAcross="2" ss:StyleID="meta"><Data ss:Type="String">Vehicle No.: ${escapeXml(challan.vehicle_no || 'NA')}</Data></Cell>
    </Row>
    ${challan.mode_of_transport ? `<Row><Cell ss:MergeAcross="2" ss:StyleID="text"/><Cell ss:MergeAcross="2" ss:StyleID="meta"><Data ss:Type="String">Mode of Transport: ${escapeXml(challan.mode_of_transport)}</Data></Cell></Row>` : ''}
    ${poRef ? `<Row><Cell ss:MergeAcross="2" ss:StyleID="text"/><Cell ss:MergeAcross="2" ss:StyleID="meta"><Data ss:Type="String">PO No : ${escapeXml(poRef)}</Data></Cell></Row>` : ''}
    ${pi?.reference ? `<Row><Cell ss:MergeAcross="2" ss:StyleID="text"/><Cell ss:MergeAcross="2" ss:StyleID="meta"><Data ss:Type="String">Ref PI No : ${escapeXml(pi.reference)}</Data></Cell></Row>` : ''}
    <Row ss:Height="8" />
    <Row ss:Height="20">
        <Cell ss:MergeAcross="2" ss:StyleID="headerCol"><Data ss:Type="String">${challan.show_bill_to === false ? 'Ship To:' : 'Bill To:'}</Data></Cell>
        <Cell ss:MergeAcross="2" ss:StyleID="headerCol"><Data ss:Type="String">Contact Person : ${contactPerson}</Data></Cell>
    </Row>
    <Row>
        <Cell ss:MergeAcross="2" ss:StyleID="boldCompany"><Data ss:Type="String">${billToName}</Data></Cell>
        <Cell ss:MergeAcross="2" ss:StyleID="text"><Data ss:Type="String">Contact No. : ${contactNo}</Data></Cell>
    </Row>
    <Row>
        <Cell ss:MergeAcross="2" ss:StyleID="addr"><Data ss:Type="String">${billToAddr}</Data></Cell>
        <Cell ss:MergeAcross="2" ss:StyleID="text"><Data ss:Type="String">GSTIN/UIN: ${gstin}</Data></Cell>
    </Row>
    <Row>
        <Cell ss:MergeAcross="2" ss:StyleID="text"><Data ss:Type="String">Place of supply: ${placeOfSupply}</Data></Cell>
        ${challan.delivery_terms ? `<Cell ss:MergeAcross="2" ss:StyleID="text"><Data ss:Type="String">Delivery Terms : ${escapeXml(challan.delivery_terms)}</Data></Cell>` : ''}
    </Row>
    <Row ss:Height="8" />
    <Row ss:Height="22">
        <Cell ss:StyleID="tableHeader"><Data ss:Type="String">Sr. No.</Data></Cell>
        <Cell ss:MergeAcross="2" ss:StyleID="tableHeader"><Data ss:Type="String">Product Description</Data></Cell>
        <Cell ss:MergeAcross="1" ss:StyleID="tableHeader"><Data ss:Type="String">Quantity</Data></Cell>
    </Row>
    `;

    items.forEach((item: any, i: number) => {
        const desc = escapeXml(item.description);
        const sub = (item.sub_items || []).map((s: any) => `${escapeXml(s.text || s)}${s.qty_note ? ` TOTAL ${escapeXml(s.qty_note)}` : ''}`);
        const hsn = item.hsn_code ? `HSN Code: ${escapeXml(item.hsn_code)}` : '';
        const fullDesc = [desc, ...sub, hsn].filter(Boolean).join('&#10;');
        const qtyFormatted = `${Number(item.quantity) || 0} ${formatUom(item.unit)}`;
        rowsXml += `
        <Row>
            <Cell ss:StyleID="tableCellCenter"><Data ss:Type="Number">${i + 1}</Data></Cell>
            <Cell ss:MergeAcross="2" ss:StyleID="tableCellLeft"><Data ss:Type="String">${fullDesc}</Data></Cell>
            <Cell ss:MergeAcross="1" ss:StyleID="tableCellCenter"><Data ss:Type="String">${qtyFormatted}</Data></Cell>
        </Row>
        `;
    });

    rowsXml += `
    <Row ss:Height="22">
        <Cell ss:MergeAcross="3" ss:StyleID="tableTotalLabel"><Data ss:Type="String">Total Quantity</Data></Cell>
        <Cell ss:MergeAcross="1" ss:StyleID="tableTotalVal"><Data ss:Type="String">${totalQty} ${formatUom(items[0]?.unit)}</Data></Cell>
    </Row>
    <Row ss:Height="12" />
    <Row>
        <Cell ss:MergeAcross="5" ss:StyleID="declaration"><Data ss:Type="String">${legalRef}</Data></Cell>
    </Row>
    <Row>
        <Cell ss:MergeAcross="5" ss:StyleID="declaration"><Data ss:Type="String">Goods are transported under Delivery Challan for dispatch against the above-mentioned invoice / order (part supply). No sale is involved in this movement.</Data></Cell>
    </Row>
    <Row>
        <Cell ss:MergeAcross="5" ss:StyleID="declaration"><Data ss:Type="String">Declaration: Delivery Challan issued under Rule 55 of CGST Rules for transportation of goods without invoice.</Data></Cell>
    </Row>
    <Row ss:Height="24" />
    <Row>
        <Cell ss:MergeAcross="2" ss:StyleID="signatory"><Data ss:Type="String">Receivers Stamp &amp; Signature</Data></Cell>
        <Cell ss:MergeAcross="2" ss:StyleID="signatory"><Data ss:Type="String">Authorised Signature&#10;(Rovasys Industries)</Data></Cell>
    </Row>
    <Row ss:Height="12" />
    <Row>
        <Cell ss:MergeAcross="5" ss:StyleID="footerBanner"><Data ss:Type="String">Thank You for Considering Rovasys Industries</Data></Cell>
    </Row>
    `;

    const excelXml = `<?xml version="1.0" encoding="UTF-8"?>
    <?mso-application progid="Excel.Sheet"?>
    <Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
        xmlns:o="urn:schemas-microsoft-com:office:office"
        xmlns:x="urn:schemas-microsoft-com:office:excel"
        xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
        xmlns:html="http://www.w3.org/TR/REC-html40">
        <Styles>
            <Style ss:ID="Default" ss:Name="Normal">
                <Alignment ss:Vertical="Center"/>
                <Font ss:FontName="Calibri" ss:Size="11" ss:Color="#000000"/>
            </Style>
            <Style ss:ID="title">
                <Alignment ss:Horizontal="Right" ss:Vertical="Center"/>
                <Font ss:FontName="Calibri" ss:Size="18" ss:Bold="1" ss:Color="#004B91"/>
            </Style>
            <Style ss:ID="boldCompany">
                <Alignment ss:Vertical="Center"/>
                <Font ss:FontName="Calibri" ss:Size="12" ss:Bold="1" ss:Color="#0A2540"/>
            </Style>
            <Style ss:ID="headerCol">
                <Alignment ss:Vertical="Center"/>
                <Font ss:FontName="Calibri" ss:Size="11" ss:Bold="1" ss:Color="#000000"/>
            </Style>
            <Style ss:ID="text">
                <Alignment ss:Vertical="Center"/>
                <Font ss:FontName="Calibri" ss:Size="10" ss:Color="#333333"/>
            </Style>
            <Style ss:ID="meta">
                <Alignment ss:Vertical="Center"/>
                <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#111111"/>
            </Style>
            <Style ss:ID="addr">
                <Alignment ss:Vertical="Top" ss:WrapText="1"/>
                <Font ss:FontName="Calibri" ss:Size="10" ss:Color="#333333"/>
            </Style>
            <Style ss:ID="tableHeader">
                <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
                <Font ss:FontName="Calibri" ss:Size="11" ss:Bold="1" ss:Color="#FFFFFF"/>
                <Interior ss:Color="#003366" ss:Pattern="Solid"/>
            </Style>
            <Style ss:ID="tableTotalLabel">
                <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
                <Font ss:FontName="Calibri" ss:Size="11" ss:Bold="1" ss:Color="#FFFFFF"/>
                <Interior ss:Color="#003366" ss:Pattern="Solid"/>
            </Style>
            <Style ss:ID="tableTotalVal">
                <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
                <Font ss:FontName="Calibri" ss:Size="11" ss:Bold="1" ss:Color="#FFFFFF"/>
                <Interior ss:Color="#003366" ss:Pattern="Solid"/>
            </Style>
            <Style ss:ID="tableCellCenter">
                <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
                <Font ss:FontName="Calibri" ss:Size="10" ss:Color="#000000"/>
                <Borders>
                    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#E5E7EB"/>
                </Borders>
            </Style>
            <Style ss:ID="tableCellLeft">
                <Alignment ss:Horizontal="Left" ss:Vertical="Center" ss:WrapText="1"/>
                <Font ss:FontName="Calibri" ss:Size="10" ss:Color="#000000"/>
                <Borders>
                    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#E5E7EB"/>
                </Borders>
            </Style>
            <Style ss:ID="declaration">
                <Alignment ss:Vertical="Center"/>
                <Font ss:FontName="Calibri" ss:Size="9" ss:Italic="1" ss:Color="#555555"/>
            </Style>
            <Style ss:ID="signatory">
                <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
                <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#111111"/>
            </Style>
            <Style ss:ID="footerBanner">
                <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
                <Font ss:FontName="Calibri" ss:Size="11" ss:Bold="1" ss:Color="#000000"/>
            </Style>
        </Styles>
        <Worksheet ss:Name="${escapeXml(ref)}">
            <Table ss:DefaultColumnWidth="75" ss:DefaultRowHeight="18">
                <Column ss:Index="1" ss:Width="50"/>
                <Column ss:Index="2" ss:Width="160"/>
                <Column ss:Index="3" ss:Width="120"/>
                <Column ss:Index="4" ss:Width="90"/>
                <Column ss:Index="5" ss:Width="80"/>
                <Column ss:Index="6" ss:Width="60"/>
                ${rowsXml}
            </Table>
        </Worksheet>
    </Workbook>`;

    const blob = new Blob([excelXml], { type: 'application/vnd.ms-excel;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Delivery_Challan_${ref.replace(/[/\\?%*:|"<>]/g, '_')}.xls`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

// ─── CSS (Modern Royal-Blue Theme matching Image 2) ──────────────────────────
const css = `
  .dc-wrap {
    font-family: Arial, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    font-size: 11px;
    color: #111;
    background: #eef1f6;
    min-height: 100vh;
    padding: 24px 16px 48px;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .dc-sheet {
    background: #fff;
    max-width: 860px;
    margin: 0 auto;
    padding: 36px 44px 36px;
    box-shadow: 0 4px 24px rgba(0,0,0,0.08);
    position: relative;
    box-sizing: border-box;
    border: 1px solid #e2e8f0;
    min-height: 1040px;
    display: flex;
    flex-direction: column;
  }

  /* ── TOP HEADER (Logo & Company on Left | Delivery Challan & Meta on Right) ── */
  .dc-header-bar {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 20px;
    padding-bottom: 18px;
  }
  .dc-header-left {
    flex: 1;
    max-width: 460px;
  }
  .dc-logo-img {
    width: 95px;
    height: auto;
    object-fit: contain;
    margin-bottom: 10px;
    display: block;
  }
  .dc-company-name {
    font-size: 18px;
    font-weight: 800;
    color: #0b2540;
    letter-spacing: -0.01em;
    line-height: 1.2;
    margin-bottom: 4px;
  }
  .dc-company-addr {
    font-size: 11px;
    color: #222;
    line-height: 1.5;
  }
  .dc-dispatch-from {
    margin-top: 3px;
    font-size: 10.5px;
    font-weight: 600;
    color: #444;
  }

  .dc-header-right {
    text-align: right;
    min-width: 280px;
  }
  .dc-title {
    font-size: 28px;
    font-weight: 800;
    color: #004b91;
    letter-spacing: -0.02em;
    line-height: 1.1;
  }
  .dc-challan-no {
    font-size: 15px;
    font-weight: 800;
    color: #000;
    margin-top: 6px;
    margin-bottom: 12px;
    letter-spacing: -0.01em;
  }
  .dc-meta-list {
    display: flex;
    flex-direction: column;
    gap: 3px;
    font-size: 11.5px;
    line-height: 1.45;
  }
  .dc-meta-row {
    display: flex;
    justify-content: flex-end;
    gap: 6px;
  }
  .dc-meta-lbl {
    font-weight: 700;
    color: #000;
    white-space: nowrap;
  }
  .dc-meta-val {
    color: #111;
    white-space: nowrap;
  }

  /* ── PARTIES / CUSTOMER DETAILS ── */
  .dc-parties-section {
    margin-top: 20px;
    margin-bottom: 18px;
  }
  .dc-parties-main-grid {
    display: grid;
    grid-template-columns: 1.15fr 0.85fr;
    gap: 20px;
    align-items: flex-start;
  }
  .dc-bill-to-title {
    font-size: 13px;
    font-weight: 800;
    color: #000;
    margin-bottom: 2px;
  }
  .dc-customer-name {
    font-size: 13px;
    font-weight: 800;
    color: #000;
    line-height: 1.3;
    margin-bottom: 2px;
  }
  .dc-customer-addr {
    font-size: 11.5px;
    color: #222;
    line-height: 1.45;
    white-space: pre-line;
  }
  .dc-ship-to-block {
    margin-top: 8px;
    padding-top: 6px;
    border-top: 1px dashed #cbd5e1;
  }

  .dc-contact-block {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 11.5px;
    line-height: 1.45;
  }
  .dc-contact-row {
    display: flex;
    align-items: baseline;
    gap: 6px;
  }
  .dc-contact-lbl {
    font-weight: 700;
    color: #000;
    white-space: nowrap;
  }
  .dc-contact-val {
    color: #111;
  }

  .dc-parties-footer {
    margin-top: 10px;
    font-size: 11.5px;
    line-height: 1.5;
  }
  .dc-gstin-line {
    font-weight: 700;
    color: #000;
  }
  .dc-supply-line {
    font-weight: 700;
    color: #000;
  }

  /* ── ITEMS TABLE ── */
  .dc-table-wrap {
    width: 100%;
    margin-top: 12px;
  }
  .dc-items-tbl {
    width: 100%;
    border-collapse: collapse;
    font-size: 11.5px;
  }
  .dc-items-tbl thead tr {
    background: #003366;
    color: #fff;
  }
  .dc-items-tbl th {
    padding: 8px 12px;
    font-size: 11.5px;
    font-weight: 700;
    color: #ffffff;
    border: none;
    letter-spacing: 0.02em;
  }
  .dc-items-tbl th.center { text-align: center; }
  .dc-items-tbl th.left { text-align: left; }
  .dc-items-tbl th.right { text-align: right; }

  .dc-items-tbl td {
    padding: 10px 12px;
    border: none;
    vertical-align: middle;
    color: #111;
    font-size: 11.5px;
  }
  .dc-items-tbl td.center { text-align: center; }
  .dc-items-tbl td.left { text-align: left; }
  .dc-items-tbl td.right { text-align: right; }

  .dc-item-desc-title {
    font-weight: 700;
    font-size: 12px;
    color: #000;
  }
  .dc-item-sub-spec {
    font-size: 10.5px;
    color: #444;
    margin-top: 2px;
  }
  .dc-item-hsn {
    font-size: 10px;
    color: #666;
    margin-top: 2px;
  }

  /* Total Bar (Solid Royal Blue matching Image 2) */
  .dc-total-row {
    background: #003366 !important;
    color: #ffffff !important;
    font-weight: 700;
    font-size: 12.5px;
  }
  .dc-total-row td {
    padding: 8px 14px !important;
    color: #ffffff !important;
    font-weight: 700;
    border: none !important;
  }

  /* ── LEGAL / REFERENCE SECTION ── */
  .dc-legal-section {
    margin-top: 18px;
    font-size: 9.5px;
    line-height: 1.45;
    color: #555;
  }
  .dc-legal-ref {
    font-weight: 700;
    color: #222;
    margin-bottom: 2px;
  }
  .dc-legal-text {
    color: #444;
  }

  /* ── SIGNATORIES SECTION ── */
  .dc-signatory-wrap {
    margin-top: auto;
    padding-top: 40px;
  }
  .dc-signatory-grid {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    padding: 0 10px;
  }
  .dc-sign-left {
    text-align: left;
    padding-bottom: 4px;
  }
  .dc-sign-right {
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .dc-sign-lbl {
    font-weight: 700;
    font-size: 12px;
    color: #000;
  }
  .dc-sign-sub {
    font-size: 11px;
    color: #000;
    margin-top: 2px;
  }
  .dc-stamp-img {
    height: 76px;
    width: auto;
    object-fit: contain;
    margin-bottom: 4px;
    display: block;
  }

  /* ── FOOTER BANNER ── */
  .dc-footer-banner {
    text-align: center;
    font-weight: 700;
    font-size: 11px;
    color: #000;
    margin-top: 36px;
    letter-spacing: 0.02em;
  }

  /* ── PRINT RULES (Calibrated for single page A4 portrait) ── */
  @media print {
    html, body {
      background: #fff !important;
      margin: 0 !important;
      padding: 0 !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    body * {
      visibility: hidden;
    }
    .dc-wrap, .dc-wrap * {
      visibility: visible;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    .dc-wrap {
      position: absolute;
      left: 0;
      top: 0;
      width: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
      background: #fff !important;
    }
    .dc-sheet {
      box-shadow: none !important;
      border: none !important;
      max-width: 100% !important;
      width: 100% !important;
      margin: 0 !important;
      padding: 8mm 10mm !important;
      min-height: calc(297mm - 20mm) !important;
    }
    @page {
      size: A4 portrait;
      margin: 6mm;
    }
    .print-hide {
      display: none !important;
    }
    .dc-items-tbl thead tr,
    .dc-total-row {
      background: #003366 !important;
      color: #ffffff !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    .dc-items-tbl tr {
      page-break-inside: avoid;
    }
    .dc-signatory-wrap, .dc-footer-banner {
      page-break-inside: avoid;
      break-inside: avoid;
    }
  }
`;

// ─── POD Capture Dialog ───────────────────────────────────────────────────────
const PODCaptureDialog = ({ challan }: { challan: DeliveryChallan }) => {
    const [open, setOpen] = useState(false);
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [sign, setSign] = useState('');
    const [notes, setNotes] = useState('');

    const notify = useNotify();
    const refresh = useRefresh();
    const [update, { isPending }] = useUpdate();

    const handleSave = () => {
        update('delivery_challans', {
            id: challan.id,
            data: { status: 'delivered', pod_date: date, pod_signature: sign, pod_notes: notes },
            previousData: challan
        }, {
            onSuccess: () => {
                notify('POD Captured successfully', { type: 'success' });
                setOpen(false);
                refresh();
            },
            onError: (error: any) => {
                notify(`Failed to capture POD: ${error?.message || 'Unknown error'}`, { type: 'error' });
            }
        });
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="bg-[#003366] hover:bg-[#003366]/90 text-white font-bold">
                    <PackageCheck className="w-4 h-4 mr-2" />
                    Capture POD
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader><DialogTitle>Capture Proof of Delivery</DialogTitle></DialogHeader>
                <div className="space-y-4 py-4">
                    <div className="space-y-2">
                        <Label>Delivery Date</Label>
                        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                        <Label>Received By (Name/Signature)</Label>
                        <Input placeholder="Enter recipient name" value={sign} onChange={(e) => setSign(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                        <Label>Remarks / Internal Notes</Label>
                        <Input placeholder="Any delivery notes..." value={notes} onChange={(e) => setNotes(e.target.value)} />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={handleSave} disabled={isPending} className="bg-emerald-600 text-white hover:bg-emerald-700">
                        {isPending ? 'Saving...' : 'Confirm Delivery'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// ─── Title (Back Button + Name) ───────────────────────────────────────────────
const DCShowTitle = () => {
    const record = useRecordContext();
    if (!record) return null;
    return (
        <div className="flex items-center gap-3">
            <Button asChild variant="outline" size="sm" className="gap-1.5 bg-[#00a68d]/10 hover:bg-[#00a68d]/20 text-[#00a68d] hover:text-[#00947e] border border-[#00a68d]/20 hover:border-[#00a68d]/30 font-semibold cursor-pointer h-9 px-4 rounded-lg">
                <Link to="/delivery_challans" className="flex items-center gap-1.5">
                    <ArrowLeft className="w-4 h-4 text-[#00a68d]" /> Back to Delivery Challans
                </Link>
            </Button>
            <span className="text-xl font-bold tracking-tight">Delivery Challan {record.reference}</span>
        </div>
    );
};

// ─── Actions Bar ──────────────────────────────────────────────────────────────
const DCShowActions = ({ onEmailClick, showWatermark, onToggleWatermark }: {
    onEmailClick: () => void;
    showWatermark: boolean;
    onToggleWatermark: () => void;
}) => {
    const record = useRecordContext() as DeliveryChallan;
    const { data: company } = useGetOne<Company>('companies', { id: record?.company_id }, { enabled: !!record?.company_id });
    const { data: contact } = useGetOne<Contact>('contacts', { id: record?.contact_id }, { enabled: !!record?.contact_id });
    const { data: pi } = useGetOne<any>('proforma_invoices', { id: record?.pi_id }, { enabled: !!record?.pi_id });
    const dataProvider = useDataProvider<CrmDataProvider>();
    const notify = useNotify();
    const refresh = useRefresh();
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);

    const handleGenerateInvoice = async () => {
        if (!record) return;
        setBusy(true);
        try {
            const result = await dataProvider.autoInvoiceFromDC([record.id as number]);
            const invoicedId = result.invoiceIds[0];
            if (invoicedId) {
                notify('Invoice generated.', { type: 'success' });
                navigate(`/customer_invoices/${invoicedId}/show`);
                return;
            }
            const errMsg = result.errors?.[0]?.error ?? 'Unknown error';
            notify(`Failed to generate invoice: ${errMsg}`, { type: 'error' });
            refresh();
        } catch (error) {
            notify(`Failed: ${(error as Error).message}`, { type: 'error' });
        } finally {
            setBusy(false);
        }
    };

    const canInvoice = record && (record.status === 'sent' || record.status === 'delivered');

    return (
        <div className="flex items-center gap-2">
            <Button
                variant={showWatermark ? 'secondary' : 'outline'}
                size="sm"
                onClick={onToggleWatermark}
                className={showWatermark ? 'bg-emerald-600 text-white hover:bg-emerald-700' : ''}
            >
                {showWatermark ? <Eye className="w-4 h-4 mr-2" /> : <EyeOff className="w-4 h-4 mr-2" />}
                Watermark: {showWatermark ? 'ON' : 'OFF'}
            </Button>
            <EditButton />
            <Button variant="outline" size="sm" onClick={onEmailClick}>
                <Mail className="w-4 h-4 mr-2" />
                Send Email
            </Button>
            <Button
                variant="outline"
                size="sm"
                onClick={() => exportChallanToExcel(record, company, pi, contact)}
                title="Download formatted Excel (.xls) matching the layout"
            >
                <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" />
                Export Excel
            </Button>
            <DCPDFButton />
            {record?.status === 'sent' && <PODCaptureDialog challan={record} />}
            {canInvoice && (
                <Button
                    size="sm"
                    onClick={handleGenerateInvoice}
                    disabled={busy}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                >
                    <FileText className="w-4 h-4 mr-2" />
                    {busy ? 'Generating…' : 'Generate Invoice'}
                </Button>
            )}
        </div>
    );
};

// ─── Show Content (Exact Layout matching Image 2) ────────────────────────────
const DCShowContent = ({ showWatermark }: { showWatermark: boolean }) => {
    const record = useRecordContext() as DeliveryChallan;
    const config = useConfigurationContext();

    const { data: company } = useGetOne<Company>('companies', { id: record?.company_id }, { enabled: !!record?.company_id });
    const { data: contact } = useGetOne<Contact>('contacts', { id: record?.contact_id }, { enabled: !!record?.contact_id });
    const { data: pi } = useGetOne<any>('proforma_invoices', { id: record?.pi_id }, { enabled: !!record?.pi_id });

    if (!record) return null;

    const items: any[] = record.items ?? [];
    const hasRates = items.some(it => (Number(it.unit_price || it.rate || 0) > 0) || (Number(it.line_total || it.amount || 0) > 0));
    const totalQty = items.reduce((acc, it) => acc + (Number(it.quantity) || 0), 0);
    const totalWeight = (record as any).total_weight || (record as any).weight;
    const totalAmount = items.reduce((acc, it) => {
        const itemTotal = Number(it.line_total || it.amount || 0);
        if (itemTotal > 0) return acc + itemTotal;
        const calc = (Number(it.quantity) || 0) * (Number(it.unit_price || it.rate) || 0);
        return acc + calc;
    }, 0);

    // Bill To details
    const billToName = company?.name || (record as any).company_name || '—';
    const showBillTo = record.show_bill_to !== false;
    const showShipTo = !!record.show_ship_to;
    const shipToAddr = cleanAddressText(record.ship_to_address) ||
        (cleanAddressText(company?.shipping_address).length > 12 ? cleanAddressText(company?.shipping_address) : '') ||
        cleanAddressText(company?.billing_address) || '—';
    const billToAddr = cleanAddressText(record.bill_to_address) ||
        cleanAddressText(company?.billing_address) || [
        cleanAddressText(company?.address),
        [company?.city, company?.state_abbr, company?.zipcode].filter(Boolean).join(', '),
        company?.country
    ].filter(Boolean).join('\n') || '—';
    const gstin = company?.tax_identifier || (company as any)?.gst_number || '—';

    // Contact person & phone
    const contactName = record.contact_person || (contact ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() : '') || '—';
    const contactPhone = record.contact_no || contact?.phone_jsonb?.[0]?.number || (contact as any)?.phone || '—';

    // Place of Supply
    const placeOfSupply = (record as any).place_of_supply ||
        [company?.city, company?.state_abbr ? `${company.state_abbr}-${company.zipcode || ''}` : company?.zipcode].filter(Boolean).join(', ') ||
        '—';

    return (
        <>
            <style>{css}</style>
            <div className="dc-wrap">
                <div className="dc-sheet">
                    <WatermarkOverlay isVisible={showWatermark} />

                    {/* ── TOP HEADER (Logo + Company | Delivery Challan + Meta) ── */}
                    <div className="dc-header-bar">
                        <div className="dc-header-left">
                            <img src="/logos/rovasys_clean.png" alt="Rovasys" className="dc-logo-img" />
                            <div className="dc-company-name">Rovasys Industries</div>
                            <div className="dc-company-addr">
                                W 145, S BLOCK, MIDC BHOSARI, PUNE – 411026<br />
                                GST NUMBER: 27ABCFR8887R1ZX<br />
                                Contact No : +91 8657555553
                            </div>
                            {(record as any).dispatch_from && (
                                <div className="dc-dispatch-from">
                                    Dispatch From: {(record as any).dispatch_from}
                                </div>
                            )}
                        </div>

                        <div className="dc-header-right">
                            <div className="dc-title">Delivery Challan</div>
                            <div className="dc-challan-no">Challan No - {record.reference}</div>

                            <div className="dc-meta-list">
                                <div className="dc-meta-row">
                                    <span className="dc-meta-lbl">Challan Date:</span>
                                    <span className="dc-meta-val">{formatDateIN(record.challan_date)}</span>
                                </div>
                                <div className="dc-meta-row">
                                    <span className="dc-meta-lbl">Challan Type:</span>
                                    <span className="dc-meta-val">{record.challan_type || 'Direct'}</span>
                                </div>
                                <div className="dc-meta-row">
                                    <span className="dc-meta-lbl">Vehicle No.:</span>
                                    <span className="dc-meta-val">{record.vehicle_no || 'NA'}</span>
                                </div>
                                {record.mode_of_transport && (
                                    <div className="dc-meta-row">
                                        <span className="dc-meta-lbl">Mode of Transport:</span>
                                        <span className="dc-meta-val">{record.mode_of_transport}</span>
                                    </div>
                                )}
                                {(record as any).po_reference && (
                                    <div className="dc-meta-row">
                                        <span className="dc-meta-lbl">PO No :</span>
                                        <span className="dc-meta-val">{(record as any).po_reference}</span>
                                    </div>
                                )}
                                {pi?.reference && (
                                    <div className="dc-meta-row">
                                        <span className="dc-meta-lbl">Ref PI No :</span>
                                        <span className="dc-meta-val">{pi.reference}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── PARTIES / CUSTOMER DETAILS (Bill To, Contact, GSTIN, Place of Supply) ── */}
                    <div className="dc-parties-section">
                        <div className="dc-parties-main-grid">
                            <div>
                                {(showBillTo || !showShipTo) && (
                                    <>
                                        <div className="dc-bill-to-title">Bill To:</div>
                                        <div className="dc-customer-name">{billToName}</div>
                                        <div className="dc-customer-addr">{billToAddr}</div>
                                    </>
                                )}
                                {showShipTo && (
                                    <div className={showBillTo ? 'dc-ship-to-block' : undefined}>
                                        <div className="dc-bill-to-title">Ship To:</div>
                                        <div className="dc-customer-name">{billToName}</div>
                                        <div className="dc-customer-addr">{shipToAddr}</div>
                                    </div>
                                )}
                            </div>

                            <div className="dc-contact-block">
                                <div className="dc-contact-row">
                                    <span className="dc-contact-lbl">Contact Person :</span>
                                    <span className="dc-contact-val">{contactName}</span>
                                </div>
                                <div className="dc-contact-row">
                                    <span className="dc-contact-lbl">Contact No. :</span>
                                    <span className="dc-contact-val">{contactPhone}</span>
                                </div>
                                {record.delivery_terms && (
                                    <div className="dc-contact-row">
                                        <span className="dc-contact-lbl">Delivery Terms :</span>
                                        <span className="dc-contact-val">{record.delivery_terms}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="dc-parties-footer">
                            <div className="dc-gstin-line">
                                GSTIN/UIN: <span style={{ fontWeight: 'normal' }}>{gstin}</span>
                            </div>
                            <div className="dc-supply-line">
                                Place of supply: <span style={{ fontWeight: 'normal' }}>{placeOfSupply}</span>
                            </div>
                        </div>
                    </div>

                    {/* ── ITEMS TABLE (Deep Navy Header, Clean Rows, Navy Total Bar) ── */}
                    <div className="dc-table-wrap">
                        <table className="dc-items-tbl">
                            <thead>
                                <tr>
                                    <th className="center" style={{ width: 65 }}>Sr. No.</th>
                                    <th className="center">Product Description</th>
                                    <th className="center" style={{ width: 140 }}>Quantity</th>
                                    {hasRates && (
                                        <>
                                            <th className="right" style={{ width: 100 }}>Rate</th>
                                            <th className="right" style={{ width: 110 }}>Amount</th>
                                        </>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item: any, i: number) => {
                                    const rate = Number(item.unit_price || item.rate || 0);
                                    const amount = Number(item.line_total || item.amount || (rate * Number(item.quantity || 0)));
                                    const qtyDisplay = `${item.quantity} ${formatUom(item.unit)}`;
                                    return (
                                        <tr key={i}>
                                            <td className="center">{i + 1}</td>
                                            <td className="center">
                                                <div className="dc-item-desc-title">{item.description}</div>
                                                {(item.sub_items ?? []).map((sub: any, si: number) => (
                                                    <div key={si} className="dc-item-sub-spec">
                                                        {sub.text ?? sub}
                                                        {sub.qty_note && <strong style={{ marginLeft: 6 }}>TOTAL {sub.qty_note}</strong>}
                                                    </div>
                                                ))}
                                                {item.hsn_code && (
                                                    <div className="dc-item-hsn">HSN Code: {item.hsn_code}</div>
                                                )}
                                            </td>
                                            <td className="center" style={{ fontWeight: 700 }}>
                                                {qtyDisplay}
                                            </td>
                                            {hasRates && (
                                                <>
                                                    <td className="right">
                                                        {rate > 0 ? `₹${rate.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—'}
                                                    </td>
                                                    <td className="right">
                                                        {amount > 0 ? `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—'}
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr className="dc-total-row">
                                    <td colSpan={2} style={{ textAlign: 'center' }}>
                                        {totalWeight ? 'Total Weight' : 'Total Quantity'}
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                        {totalWeight ? `${totalWeight} KG` : `${totalQty} ${formatUom(items[0]?.unit)}`}
                                    </td>
                                    {hasRates && (
                                        <>
                                            <td></td>
                                            <td className="right" style={{ textAlign: 'right' }}>
                                                ₹{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                            </td>
                                        </>
                                    )}
                                </tr>
                                {hasRates && totalAmount > 0 && (
                                    <tr>
                                        <td colSpan={hasRates ? 5 : 3} style={{ background: '#f8fafc', padding: '6px 12px', fontSize: 10.5, borderTop: '1px solid #e2e8f0' }}>
                                            <strong>Amount in Words:</strong> {numberToWordsIndian(totalAmount)}
                                        </td>
                                    </tr>
                                )}
                            </tfoot>
                        </table>
                    </div>

                    {/* ── LEGAL REFERENCE & DECLARATION ── */}
                    <div className="dc-legal-section">
                        <div className="dc-legal-ref">
                            {pi?.reference ? (
                                <>Reference: Material dispatched against Proforma Invoice No: {pi.reference} – Part Supply</>
                            ) : record.terms_conditions ? (
                                <>Reference: {record.terms_conditions}</>
                            ) : (
                                <>Reference: Material dispatched under Delivery Challan – Part Supply</>
                            )}
                        </div>
                        <div className="dc-legal-text">
                            Goods are transported under Delivery Challan for dispatch against the above-mentioned invoice / order (part supply). No sale is involved in this movement.<br />
                            Declaration: Delivery Challan issued under Rule 55 of CGST Rules for transportation of goods without invoice.
                        </div>
                    </div>

                    {/* ── SIGNATORIES SECTION ── */}
                    <div className="dc-signatory-wrap">
                        <div className="dc-signatory-grid">
                            <div className="dc-sign-left">
                                <div className="dc-sign-lbl">Receivers Stamp &amp; Signature</div>
                            </div>

                            <div className="dc-sign-right">
                                <img src="/img/rovasys-stamp.png" alt="Company Stamp" className="dc-stamp-img" />
                                <div className="dc-sign-lbl">Authorised Signature</div>
                                <div className="dc-sign-sub">(Rovasys Industries)</div>
                            </div>
                        </div>

                        {/* ── FOOTER BANNER ── */}
                        <div className="dc-footer-banner">
                            Thank You for Considering Rovasys Industries
                        </div>
                    </div>
                </div>

                {/* Footer metadata on screen */}
                <div className="print-hide flex justify-between text-xs text-slate-500 max-w-[860px] mx-auto mt-3 px-2">
                    <span>Delivery Challan: {record.reference}</span>
                    {config.showPrintTimestamp && (
                        <span>Print Date: {new Date().toLocaleDateString('en-IN')} {new Date().toLocaleTimeString('en-IN')}</span>
                    )}
                </div>
            </div>
        </>
    );
};

// ─── Export ───────────────────────────────────────────────────────────────────
export const DCShow = () => {
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [showWatermark, setShowWatermark] = useState(false);

    return (
        <Show
            title={<DCShowTitle />}
            actions={
                <DCShowActions
                    onEmailClick={() => setEmailModalOpen(true)}
                    showWatermark={showWatermark}
                    onToggleWatermark={() => setShowWatermark(!showWatermark)}
                />
            }
        >
            <DCShowContent showWatermark={showWatermark} />
            <DCEmailModal open={emailModalOpen} onClose={() => setEmailModalOpen(false)} />
        </Show>
    );
};
